import axios from 'axios';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createContext } from 'use-context-selector';

import { useCustomNavigate } from 'watchtower-ui/components/common/CustomNavigation';
import { withErrorHandler } from 'watchtower-ui/components/common/ErrorBoundary/ErrorBoundary';
import { FullScreenLoader } from 'watchtower-ui/components/common/Spinner';
import { useAuth } from 'watchtower-ui/contexts/AuthContext';
import type { CountryISO, TCountryConfigMap } from 'watchtower-ui/types/country';
import {
  COMING_SOON_COUNTRIES,
  COUNTRY_CONFIG_MAP,
  DEFAULT_COUNTRY,
  DEFAULT_COUNTRY_CONFIG_MAP,
} from 'watchtower-ui/types/country';
import { API_URL_CO, DEFAULT_API_URL, getApiUrl, REDIRECT_URI, US_REDIRECT_URL } from 'watchtower-ui/utils/Constants';
import { countryISOToName } from 'watchtower-ui/utils/country';
import { existingURLPath } from 'watchtower-ui/utils/utilityFunctions';

const API_URL_EXCEPTIONS: Record<CountryISO, string> = {
  CO: API_URL_CO,
};

const API_URL_EXCEPTION_KEYS = Object.keys(API_URL_EXCEPTIONS);

type THandleCountryNavigateProps = {
  countryCode?: CountryISO;
  navigatePath?: string;
};

/** @internal */
export interface CountryContext {
  selectedCountry: CountryISO;
  selectedCountryName: string;
  selectedCountryPath: string;
  config: TCountryConfigMap;
  setSelectedCountry?: (code: CountryISO) => void;
  handleCountryNavigate?: (props?: THandleCountryNavigateProps) => void;
}

/**
 * Resets axios baseURL to default or country-specific URL
 */
const resetApiBaseUrl = (): void => {
  axios.defaults.baseURL = DEFAULT_API_URL;
};

/**
 * Configure axios baseURL according to country code
 */
const configureApiBaseUrl = (countryCode: CountryISO): void => {
  // Check for API URL exceptions
  if (API_URL_EXCEPTION_KEYS.includes(countryCode)) {
    const exceptionUrl = API_URL_EXCEPTIONS[countryCode];
    axios.defaults.baseURL = exceptionUrl;
    return;
  }

  // Sets dynamic baseURL using country code
  const baseUrl = getApiUrl(countryCode);
  axios.defaults.baseURL = baseUrl;
};

const Context = createContext<CountryContext>({
  selectedCountry: DEFAULT_COUNTRY,
  selectedCountryName: countryISOToName(DEFAULT_COUNTRY),
  selectedCountryPath: '',
  config: DEFAULT_COUNTRY_CONFIG_MAP,
});

const ContextManager = withErrorHandler(({ children }: PropsWithChildren) => {
  const navigate = useCustomNavigate();

  const { countryCode: countryCodeRAW } = useParams<{
    countryCode: string;
  }>();

  const { loggedIn, validCountries } = useAuth();

  const [selectedCountry, setSelectedCountry] = useState<CountryISO>(DEFAULT_COUNTRY);

  // Track whether country access check is complete to prevent flash of homepage
  const [isCountryAccessChecked, setIsCountryAccessChecked] = useState(false);
  const isRedirectingRef = useRef(false);

  const countryCode = useMemo<CountryISO | null>(() => {
    if (!countryCodeRAW) {
      return null;
    }

    return countryCodeRAW.toUpperCase();
  }, [countryCodeRAW]);

  /**
   * Memoized selected country name
   */
  const selectedCountryName = useMemo<string>(() => countryISOToName(selectedCountry), [selectedCountry]);

  /**
   * Memoized selected country path
   */
  const selectedCountryPath = useMemo<string>(() => `/${selectedCountry.toLowerCase()}`, [selectedCountry]);

  /**
   * Country based configuration
   * TODO: Most config is hardcoded on frontend, and ideally should be fetched from backend in the future.
   */
  const config = useMemo<TCountryConfigMap>(() => {
    const defaultConfig = DEFAULT_COUNTRY_CONFIG_MAP;
    const countrySpecificConfig = COUNTRY_CONFIG_MAP[selectedCountry];

    return {
      ...defaultConfig,
      ...countrySpecificConfig,
    };
  }, [selectedCountry]);

  const handleCountryNavigate = useCallback(
    (props?: THandleCountryNavigateProps) => {
      const code = props?.countryCode || selectedCountry;
      const path = props?.navigatePath || existingURLPath('append') || '';

      const shouldSetCountry = code !== selectedCountry;

      if (shouldSetCountry) {
        setSelectedCountry(code);
      }

      // Exception: If country code is US, redirect to US Watchtower
      if (code === 'US') {
        globalThis.location.href = US_REDIRECT_URL;
        return;
      }

      // Normalize REDIRECT_URI by removing trailing slash
      let baseURI = REDIRECT_URI;
      while (baseURI.endsWith('/')) {
        baseURI = baseURI.slice(0, -1);
      }
      const prefix = existingURLPath('prepend') || '';
      const countryPath = code ? `/${code.toLowerCase()}` : '';
      const linkPath = path.startsWith('/') ? path : `/${path}`;

      // Add prefix if not present (check both with and without trailing slash)
      if (prefix && !baseURI.endsWith(prefix) && !baseURI.endsWith(`${prefix}/`)) {
        baseURI = `${baseURI}${prefix}`;
      }

      globalThis.location.href = `${baseURI}${countryPath}${linkPath}`;
    },
    [selectedCountry],
  );

  /**
   * On valid countries change, ensure selected country is still valid
   */
  useEffect(() => {
    // If we are not logged in, do nothing
    if (!loggedIn) {
      return;
    }

    // If selected country is still valid, do nothing
    if (validCountries.includes(selectedCountry)) {
      return;
    }

    // If selected country is invalid, set to first valid country or default
    const newCountry = validCountries[0] || DEFAULT_COUNTRY;
    setSelectedCountry(newCountry);
  }, [loggedIn, validCountries, selectedCountry]);

  /**
   * On countryCode param change, validate and set selected country
   * and configure baseURL
   */
  useEffect(() => {
    // If we are not logged in, do nothing
    if (!loggedIn) {
      return;
    }

    // If user doesn't have access to this country, redirect to request-access page
    if (!countryCode || validCountries.length === 0 || !validCountries.includes(countryCode.toUpperCase())) {
      const targetCountry = countryCode?.toUpperCase() || '';
      resetApiBaseUrl();
      isRedirectingRef.current = true;
      if (targetCountry) {
        // Redirect to request-access with the country they tried to access
        navigate({ to: '/request-access', removeCountry: true, searchParam: `country=${targetCountry}` });
      } else {
        // No specific country, go back to country selection
        navigate({ to: '/', removeCountry: true });
      }
      return;
    }

    // Coming soon countries - redirect to country selection page
    if (COMING_SOON_COUNTRIES.includes(countryCode.toUpperCase())) {
      resetApiBaseUrl();
      isRedirectingRef.current = true;
      navigate({ to: '/', removeCountry: true });
      return;
    }

    // Exception: If country code is US, redirect to US Watchtower
    if (countryCode.toUpperCase() === 'US') {
      isRedirectingRef.current = true;
      globalThis.location.href = US_REDIRECT_URL;
      return;
    }

    configureApiBaseUrl(countryCode);
    setSelectedCountry(countryCode);
    setIsCountryAccessChecked(true);
  }, [loggedIn, validCountries, countryCode, navigate]);

  const value = useMemo(
    () => ({
      selectedCountry,
      selectedCountryName,
      selectedCountryPath,
      config,
      setSelectedCountry,
      handleCountryNavigate,
    }),
    [selectedCountry, selectedCountryName, selectedCountryPath, config, setSelectedCountry, handleCountryNavigate],
  );

  // Show loader while country access check is pending or redirecting
  if (!isCountryAccessChecked || isRedirectingRef.current) {
    return <FullScreenLoader full />;
  }

  return <Context.Provider value={value}>{children}</Context.Provider>;
});

export default {
  Context,
  ContextManager,
};
